import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, CheckCircle2, ShieldCheck } from "lucide-react";
import { useStore } from "../context/StoreContext";
import api, { img, money } from "../lib/api";
import { toast } from "sonner";

const loadRazorpay = () => new Promise((res) => {
  if (window.Razorpay) return res(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => res(true); s.onerror = () => res(false);
  document.body.appendChild(s);
});

const Field = ({ label, value, onChange, type = "text", testid, ...rest }) => (
  <div><label className="text-xs uppercase tracking-[0.12em] text-forest/55">{label}</label>
    <input type={type} value={value} onChange={onChange} data-testid={testid} className="input-line mt-1" {...rest} /></div>
);

export default function Checkout() {
  const { cart, clearCart, user, settings, setCartOpen } = useStore();
  const nav = useNavigate();
  useEffect(() => { setCartOpen(false); }, []);
  const [summary, setSummary] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced] = useState(false);
  const [service, setService] = useState(null);
  const [c, setC] = useState({ name: user?.name || "", email: user?.email || "", phone: user?.phone || "" });
  const [a, setA] = useState({ address: "", city: "", state: "", pincode: "", country: "India" });

  useEffect(() => {
    if (cart.length === 0) { if (!placed) nav("/cart"); return; }
    api.post("/checkout/validate", { items: cart.map(i => ({ slug: i.slug, quantity: i.quantity })), coupon_code: coupon || null })
      .then(r => setSummary(r.data));
  }, [cart, coupon]);

  useEffect(() => {
    if (a.pincode.length === 6) {
      api.get(`/shipping/serviceability?pincode=${a.pincode}`).then(r => setService(r.data)).catch(() => setService(null));
    } else setService(null);
  }, [a.pincode]);

  const valid = c.name && c.email && c.phone && a.address && a.city && a.state && a.pincode.length === 6;

  const placeOrder = async () => {
    if (!valid) { toast.error("Please complete all required fields"); return; }
    setPlacing(true);
    try {
      const res = await api.post("/orders", {
        items: cart.map(i => ({ slug: i.slug, quantity: i.quantity })),
        coupon_code: summary?.coupon_code || null,
        customer: c, shipping_address: a,
      });
      const order = res.data.order;
      const pay = res.data.payment;

      if (pay.mock) {
        await api.post(`/orders/${order.id}/verify-payment`, { mock: true });
        setPlaced(true);
        nav(`/order-confirmation?order=${order.order_number}`);
        clearCart();
        return;
      }
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load payment gateway"); setPlacing(false); return; }
      const rzp = new window.Razorpay({
        key: pay.key_id, amount: pay.amount, currency: pay.currency,
        name: "IAH", description: `Order ${order.order_number}`,
        order_id: pay.razorpay_order_id,
        prefill: { name: c.name, email: c.email, contact: c.phone },
        theme: { color: "#263D2B" },
        handler: async (resp) => {
          try {
            await api.post(`/orders/${order.id}/verify-payment`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setPlaced(true);
            nav(`/order-confirmation?order=${order.order_number}`);
            clearCart();
          } catch { toast.error("Payment verification failed"); setPlacing(false); }
        },
        modal: { ondismiss: () => setPlacing(false) },
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not place order"); setPlacing(false);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-14">
      <h1 className="serif text-5xl mb-10">Checkout</h1>
      <div className="grid lg:grid-cols-[1fr_400px] gap-14">
        <div className="space-y-12">
          <section>
            <div className="flex items-center gap-3 mb-6"><span className="h-7 w-7 rounded-full bg-forest text-ivory flex items-center justify-center text-xs">1</span><h2 className="serif text-2xl">Contact Information</h2></div>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full Name" value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} testid="checkout-name" />
              <Field label="Phone" value={c.phone} onChange={(e) => setC({ ...c, phone: e.target.value })} testid="checkout-phone" />
              <div className="sm:col-span-2"><Field label="Email" type="email" value={c.email} onChange={(e) => setC({ ...c, email: e.target.value })} testid="checkout-email" /></div>
            </div>
            {!user && <p className="text-xs text-forest/50 mt-3">Have an account? <Link to="/login" className="underline">Login</Link> for faster checkout.</p>}
          </section>

          <section>
            <div className="flex items-center gap-3 mb-6"><span className="h-7 w-7 rounded-full bg-forest text-ivory flex items-center justify-center text-xs">2</span><h2 className="serif text-2xl">Shipping Address</h2></div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="sm:col-span-2"><Field label="Address" value={a.address} onChange={(e) => setA({ ...a, address: e.target.value })} testid="checkout-address" /></div>
              <Field label="City" value={a.city} onChange={(e) => setA({ ...a, city: e.target.value })} testid="checkout-city" />
              <Field label="State" value={a.state} onChange={(e) => setA({ ...a, state: e.target.value })} testid="checkout-state" />
              <Field label="PIN Code" value={a.pincode} onChange={(e) => setA({ ...a, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })} testid="checkout-pincode" />
              <Field label="Country" value={a.country} onChange={(e) => setA({ ...a, country: e.target.value })} testid="checkout-country" />
            </div>
            {service && <p className={`text-xs mt-3 ${service.serviceable ? "text-moss" : "text-brown"}`} data-testid="serviceability">
              {service.serviceable ? `✓ Deliverable to ${a.pincode}${service.estimated_days ? ` · approx ${service.estimated_days} days` : ""}` : "This pincode may not be serviceable"}</p>}
          </section>

          <section>
            <div className="flex items-center gap-3 mb-6"><span className="h-7 w-7 rounded-full bg-forest text-ivory flex items-center justify-center text-xs">3</span><h2 className="serif text-2xl">Payment</h2></div>
            <div className="border border-sage/40 p-6 bg-beige/30 flex items-center gap-4">
              <Lock size={20} className="text-forest" />
              <div>
                <p className="font-medium">Razorpay Secure Checkout</p>
                <p className="text-xs text-forest/55">UPI · Cards · Netbanking · Wallets. Card details are never stored.</p>
                <p className="text-xs text-moss mt-1" data-testid="test-mode-note">Live secure payments enabled — pay via UPI, Cards, Netbanking or Wallets.</p>
              </div>
            </div>
          </section>
        </div>

        {/* Summary */}
        <aside className="bg-beige/40 p-8 h-fit lg:sticky lg:top-28">
          <h2 className="serif text-2xl mb-6">Order Summary</h2>
          <div className="space-y-4 max-h-64 overflow-auto hide-scrollbar mb-6">
            {cart.map((it) => (
              <div key={it.slug} className="flex gap-3">
                <div className="relative"><img src={img(it.image)} alt="" className="h-16 w-14 object-cover bg-ivory" /><span className="absolute -top-2 -right-2 bg-forest text-ivory text-[10px] h-5 w-5 flex items-center justify-center rounded-full">{it.quantity}</span></div>
                <div className="flex-1"><div className="text-sm serif text-lg leading-tight">{it.name}</div><div className="text-xs text-forest/50">{it.size}</div></div>
                <span className="text-sm">{money(it.price * it.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mb-5">
            <input value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Coupon" className="flex-1 border border-forest/20 bg-ivory px-3 py-2 text-sm outline-none uppercase" data-testid="checkout-coupon" />
          </div>
          <div className="space-y-2 text-sm border-t border-sage/30 pt-4">
            <div className="flex justify-between"><span className="text-forest/65">Subtotal</span><span>{money(summary?.subtotal || 0)}</span></div>
            {summary?.discount > 0 && <div className="flex justify-between text-moss"><span>Discount</span><span>− {money(summary.discount)}</span></div>}
            <div className="flex justify-between"><span className="text-forest/65">Shipping</span><span>{summary?.shipping ? money(summary.shipping) : "Free"}</span></div>
            {summary?.tax > 0 && <div className="flex justify-between"><span className="text-forest/65">Taxes</span><span>{money(summary.tax)}</span></div>}
            <div className="flex justify-between pt-3 border-t border-sage/30"><span className="serif text-xl">Total</span><span className="serif text-2xl" data-testid="checkout-total">{money(summary?.total || 0)}</span></div>
          </div>
          <button disabled={!valid || placing} onClick={placeOrder} data-testid="place-order-btn" className="btn-primary w-full mt-6 disabled:opacity-40">
            {placing ? "Processing…" : "Pay Securely"}
          </button>
          <div className="flex items-center justify-center gap-2 mt-4 text-xs text-forest/50"><ShieldCheck size={14} /> Secure · Encrypted · Trusted</div>
        </aside>
      </div>
    </div>
  );
}
